import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Moment } from 'moment';
import { Observable } from 'rxjs';

import { Snapshot, Vdd, VddWithContent } from '@vi-iot/client';

import {
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsVddListParameter,
} from '../remote-diagnostics';

import { RemoteDiagnosticsSnapshotsActions } from './remote-diagnostics-snapshots.actions';
import { SnapshotType, VddCurrentAndSnapshotValue } from './remote-diagnostics-snapshots.model';
import { RemoteDiagnosticsSnapshotsSelectors } from './remote-diagnostics-snapshots.selectors';

@Injectable({ providedIn: 'root' })
export class RemoteDiagnosticsSnapshotsStore {
  constructor(private store: Store) {}

  showFetchSnapshotDetailsLoader$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getShowFetchSnapshotDetailsLoader()
  );

  showSnapshotsLoader$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getShowSnapshotsLoader()
  );

  showCreateSnapshotLoader$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getShowCreateSnapshotLoader()
  );

  snapshots$ = this.store.select(RemoteDiagnosticsSnapshotsSelectors.getSnapshots());
  snapshotsFiltered$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getSnapshotsFiltered()
  );
  snapshotsFilteredLength$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getSnapshotsFilteredLength()
  );

  currentSnapshotType$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getCurrentSnapshotType()
  );

  installationVddList$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getInstallationVddList
  );

  getInstallationVddListWithoutSeparator$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getInstallationVddListWithoutSeparator
  );

  installationVddListLength$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getInstallationVddListLength
  );

  getSelectedParametersForNewSnapshot$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getSelectedParametersForNewSnapshot
  );

  getSelectedParametersForNewSnapshotLength$ = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getSelectedParametersForNewSnapshotLength
  );

  selectedSnapshot$ = this.store.select(RemoteDiagnosticsSnapshotsSelectors.getSelectedSnapshot());

  snapshotDetails$ = this.store.select(RemoteDiagnosticsSnapshotsSelectors.getSnapshotDetails());

  getVddsToCancel$: Observable<string[]> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getVddsToCancel
  );

  getVddsWithContent$: Observable<VddWithContent[]> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getVddsWithContent
  );

  getVddsForNewSnapshotWithContent$: Observable<VddWithContent[]> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getVddsForNewSnapshotWithContent
  );

  getVddsForBackupWithContent$: Observable<VddWithContent[]> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getVddsForBackupWithContent
  );

  getDateOfTheValuesRead$: Observable<Date> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getDateOfVddValuesRead
  );

  isAnyFromSnapshotVddListLoading$: Observable<boolean> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.isAnyFromSnapshotVddListLoading
  );

  isAnyFromBackupVddListLoading$: Observable<boolean> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.isAnyFromBackupVddListLoading
  );

  howManySnapshotVddLoaded$: Observable<number> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.howManySnapshotVddLoaded
  );

  showValues$: Observable<boolean> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.showValues
  );

  isShowingMoreSnapshots$: Observable<boolean> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.isShowingMoreSnapshots
  );

  lastNumberOfSnapshots$: Observable<number> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getLastsNumberOfSnapshots
  );

  isSnapshotCreated$: Observable<boolean> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.isSnapshotCreated
  );

  getSelectedControlUnit$: Observable<RemoteDiagnosticsControlUnit> = this.store.select(
    RemoteDiagnosticsSnapshotsSelectors.getSelectedControlUnit
  );

  getAvailableParametersExceptSelected$: Observable<
    RemoteDiagnosticsVddListParameter[]
  > = this.store.select(RemoteDiagnosticsSnapshotsSelectors.getAvailableParametersExceptSelected);

  fetchSnapshots(installationId: number): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.fetchSnapshots({
        installationId,
        snapshotType: SnapshotType.SNAPSHOT,
        wasBackupGenerated: false,
      })
    );
  }

  fetchBackups(installationId: number): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.fetchSnapshots({
        installationId,
        snapshotType: SnapshotType.BACKUP,
        wasBackupGenerated: false,
      })
    );
  }

  showValues(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.showValues());
  }

  hideValues(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.hideValues());
  }

  fetchSnapshotDetails(installationId: number, snapshotId: string): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetails({
        installationId,
        snapshotId,
      })
    );
  }

  fetchInstallationVddList(installationId: number): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDList({
        installationId,
      })
    );
  }

  createSnapshot(installationId: number, snapshotType: SnapshotType): void {
    if (snapshotType === SnapshotType.BACKUP) {
      this.store.dispatch(
        RemoteDiagnosticsSnapshotsActions.createSnapshotOfTypeBackup({
          installationId,
          snapshotType,
        })
      );
    }
    if (snapshotType === SnapshotType.SNAPSHOT) {
      this.store.dispatch(
        RemoteDiagnosticsSnapshotsActions.createSnapshotOfTypeSnapshot({
          installationId,
          snapshotType,
        })
      );
    }
  }

  selectSnapshot(snapshot: Snapshot): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.snapshotSelected({
        snapshot,
      })
    );
  }

  getVddSnapshotAndCurrentValues(
    vddName: string,
    logicalLink: string
  ): Observable<VddCurrentAndSnapshotValue> {
    return this.store.select(
      RemoteDiagnosticsSnapshotsSelectors.getVddSnapshotAndCurrentValues(vddName, logicalLink)
    );
  }

  deleteParameter(vdd: Vdd): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.deleteParameter({ vdd }));
  }

  deleteAllParameters(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.deleteAllParameters());
  }

  triggerReadVddListValuesForSnapshot(gatewaySerial: string, vdd?: Vdd): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.triggerReadVddListValuesForSnapshot({ gatewaySerial, vdd })
    );
  }

  setDateOfTheValuesRead(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.setDateOfTheValuesRead());
  }

  clearDateOfTheValuesRead(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.clearDateOfTheValuesRead());
  }

  setInstallationVddList(vdds: Vdd[]): void {
    this.store.dispatch(
      RemoteDiagnosticsSnapshotsActions.setInstallationVDDList({ vddList: vdds })
    );
  }

  addParameter(vdd: Vdd): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.addParameter({ vdd }));
  }

  selectControlUnit(controlUnit: RemoteDiagnosticsControlUnit): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.selectControlUnit({ controlUnit }));
  }

  showMoreSnapshots(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.showMoreSnapshots());
  }

  showLessSnapshots(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.showLessSnapshots());
  }

  setFilteredDate(filterDate: Moment): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.setFilterDate({ date: filterDate }));
  }

  clearState(): void {
    this.store.dispatch(RemoteDiagnosticsSnapshotsActions.clearState());
  }

  isSelectedSnapshotParameterInvalid(vddName: string, logicalLink: string): Observable<boolean> {
    return this.store.select(
      RemoteDiagnosticsSnapshotsSelectors.isSelectedSnapshotParameterInvalid(vddName, logicalLink)
    );
  }
}
